export const FormatNumber = (value: number) => {
  return new Intl.NumberFormat("sv", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Parses a string number in various Swedish/Finnish formats to a number
 * Handles formats like:
 * - 100 000 000 (spaces as thousand separator)
 * - 100,000,000 (commas as thousand separator)
 * - 100.000.000 (dots as thousand separator)
 * - 100 000,50 (Swedish/Finnish: space + comma decimal)
 * - 100.000,50 (European: dot + comma decimal)
 * - 100,000.50 (US: comma + dot decimal)
 * - 50,5 or 50.5 (simple decimal)
 */
export const formatToNumber = (
  value: string,
  type: "integer" | "float" = "integer",
): number | null => {
  if (!value || typeof value !== "string") return null;

  // Remove all whitespace and apostrophes
  const cleaned = value.trim().replace(/[\s']/g, "");

  if (!cleaned) return null;

  // Find positions of commas and dots
  const commaPositions = [...cleaned.matchAll(/,/g)].map(
    (match) => match.index,
  );
  const dotPositions = [...cleaned.matchAll(/\./g)].map((match) => match.index);

  let integerPart = "";
  let decimalPart = "";

  // Determine the format and extract parts
  if (commaPositions.length === 0 && dotPositions.length === 0) {
    // No separators - just digits
    integerPart = cleaned;
  } else if (commaPositions.length === 0 && dotPositions.length === 1) {
    // Only dots: could be decimal (12.5) or thousand separator (1000.000)
    const dotPos = dotPositions[0];
    if (dotPos === undefined) {
      integerPart = cleaned;
    } else {
      const afterDot = cleaned.slice(dotPos + 1);

      // If type is float, always treat single dot as decimal
      // If type is integer, only treat as decimal if 1-2 digits after dot
      const shouldTreatAsDecimal =
        type === "float" || /^\d{1,2}$/.test(afterDot);

      if (shouldTreatAsDecimal) {
        integerPart = cleaned.slice(0, dotPos);
        decimalPart = afterDot;
      } else {
        // Treat dot as thousand separator
        integerPart = cleaned.replace(/\./g, "");
      }
    }
  } else if (dotPositions.length === 0 && commaPositions.length === 1) {
    // Only commas: could be decimal (12,5) or thousand separator (1000,000)
    const commaPos = commaPositions[0];
    if (commaPos === undefined) {
      integerPart = cleaned;
    } else {
      const afterComma = cleaned.slice(commaPos + 1);

      // If type is float, always treat single comma as decimal
      // If type is integer, only treat as decimal if 1-2 digits after comma
      const shouldTreatAsDecimal =
        type === "float" || /^\d{1,2}$/.test(afterComma);

      if (shouldTreatAsDecimal) {
        integerPart = cleaned.slice(0, commaPos);
        decimalPart = afterComma;
      } else {
        // Treat comma as thousand separator
        integerPart = cleaned.replace(/,/g, "");
      }
    }
  } else {
    // Multiple separators - determine which is decimal
    const lastCommaPos = commaPositions[commaPositions.length - 1] || -1;
    const lastDotPos = dotPositions[dotPositions.length - 1] || -1;

    // The rightmost separator is likely the decimal separator
    if (lastCommaPos > lastDotPos) {
      // Comma is decimal separator (European format: 1.000.000,50)
      const afterComma = cleaned.slice(lastCommaPos + 1);

      if (/^\d{1,2}$/.test(afterComma)) {
        integerPart = cleaned.slice(0, lastCommaPos).replace(/[.,]/g, "");
        decimalPart = afterComma;
      } else {
        integerPart = cleaned.replace(/[.,]/g, "");
      }
    } else if (lastDotPos > lastCommaPos) {
      // Dot is decimal separator (US format: 1,000,000.50)
      const afterDot = cleaned.slice(lastDotPos + 1);

      if (/^\d{1,2}$/.test(afterDot)) {
        integerPart = cleaned.slice(0, lastDotPos).replace(/[.,]/g, "");
        decimalPart = afterDot;
      } else {
        integerPart = cleaned.replace(/[.,]/g, "");
      }
    } else {
      // Fallback: treat all as thousand separators
      integerPart = cleaned.replace(/[.,]/g, "");
    }
  }

  // Validate that we only have digits
  if (!/^\d+$/.test(integerPart)) {
    return null;
  }

  if (decimalPart && !/^\d+$/.test(decimalPart)) {
    return null;
  }

  // Construct the final number string
  const numberString = decimalPart
    ? `${integerPart}.${decimalPart}`
    : integerPart;
  const numericValue = Number.parseFloat(numberString);

  if (Number.isNaN(numericValue)) {
    return null;
  }

  return type === "integer" ? Math.round(numericValue) : numericValue;
};

// "input" to prepare data for form input to be sent to the server, (convert yearly to monthly)
// "output" to format the "output data" from the server, to be displayed in the UI (convert monthly to yearly)
export type LenderMonthlyInterestUseCase = "TO_MONTHLY" | "TO_YEARLY";

export const formatLenderMonthlyInterestRate = (
  value: number,
  useCase: LenderMonthlyInterestUseCase,
  isInterestRatePerYearActivated?: boolean,
): number => {
  let convertedValue = value;
  const DefaultRoundDecimals = 1e2;

  if (!isInterestRatePerYearActivated) {
    // Round to max 3 decimal places
    return (
      Math.round(convertedValue * DefaultRoundDecimals) / DefaultRoundDecimals
    );
  }

  switch (useCase) {
    case "TO_MONTHLY":
      convertedValue = value / 12;

      // Round to max 2 decimal places
      return (
        Math.round(convertedValue * DefaultRoundDecimals) / DefaultRoundDecimals
      );

    case "TO_YEARLY":
      convertedValue = value * 12;

      // Round to max 3 decimal places
      return (
        Math.round(convertedValue * DefaultRoundDecimals) / DefaultRoundDecimals
      );

    default: {
      const exhaustiveCheck: never = useCase;
      throw new Error(`Invalid use case: ${exhaustiveCheck}`);
    }
  }
};
